import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, watch, onMounted } from 'vue'
import { api } from '@/composables/api'

interface BulkEditFormData {
  status?: string
  location?: string
  rating?: number | null
  os?: string
  ram?: string
  storage?: string
  cpu?: string
  gpu?: string
  charger?: string
  comments?: string
  value?: number | null
  donor?: string
  donated_on?: string
}

interface Props {
  show: boolean
  itemCount: number
  submitting?: boolean
  error?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BulkEditModal',
  props: {
    show: { type: Boolean, default: false },
    itemCount: { default: 0 },
    submitting: { type: Boolean, default: false },
    error: { default: '' }
  },
  emits: ["close", "submit"],
  setup(__props: any, { emit: __emit }) {

/* eslint-disable no-undef */
const props = __props

const emit = __emit

const showModal = ref(props.show)
const formRef = ref<any>(null)
const donorOptions = ref<string[]>([])
const chargerOptions = ref<string[]>([])

const loadDonors = async () => {
  try {
    const response = await api.get('/equipment/donors/list')
    const donors = response.data || response || []
    donorOptions.value = donors.map((d: any) => d.donor).filter(Boolean)
  } catch (error) {
    console.error('Error loading donors:', error)
    donorOptions.value = []
  }
}

const loadChargers = async () => {
  try {
    const response = await api.get('/equipment/chargers/list')
    const chargers = response.data || response || []
    chargerOptions.value = chargers.map((c: any) => c.charger).filter(Boolean)
  } catch (error) {
    console.error('Error loading chargers:', error)
    chargerOptions.value = []
  }
}

onMounted(() => {
  loadDonors()
  loadChargers()
})

const localFormData = ref<BulkEditFormData>({
  status: undefined,
  location: undefined,
  rating: null,
  os: undefined,
  ram: undefined,
  storage: undefined,
  cpu: undefined,
  gpu: undefined,
  charger: undefined,
  comments: undefined,
  value: null,
  donor: undefined,
  donated_on: undefined
})

const statusOptions = [
  { title: 'Processing', value: 'processing' },
  { title: 'Troubleshooting', value: 'troubleshooting' },
  { title: 'Parts', value: 'parts' },
  { title: 'Available', value: 'available' },
  { title: 'Claimed', value: 'claimed' },
  { title: 'Delivered', value: 'delivered' },
  { title: 'Retired', value: 'retired' }
]

const osOptions = [
  'Windows',
  'macOS',
  'Chrome OS',
  'Linux',
  'Linux (Mint)',
  'Linux (Zorin)',
  'Linux (Ubuntu)'
]

watch(() => props.show, (newVal) => {
  showModal.value = newVal
  if (newVal) {
    // Reset form when modal opens
    localFormData.value = {
      status: undefined,
      location: undefined,
      rating: null,
      os: undefined,
      ram: undefined,
      storage: undefined,
      cpu: undefined,
      gpu: undefined,
      comments: undefined,
      value: null,
      donor: undefined,
      donated_on: undefined
    }
  }
})

watch(showModal, (newVal) => {
  if (!newVal) {
    emit('close')
  }
})

const handleSubmit = async () => {
  // Filter out undefined/null values - only send fields that user filled in
  const updateData: BulkEditFormData = {}

  if (localFormData.value.status !== undefined && localFormData.value.status !== null) {
    updateData.status = localFormData.value.status
  }
  if (localFormData.value.location !== undefined && localFormData.value.location !== null && localFormData.value.location !== '') {
    updateData.location = localFormData.value.location
  }
  if (localFormData.value.rating !== undefined && localFormData.value.rating !== null) {
    updateData.rating = localFormData.value.rating
  }
  if (localFormData.value.os !== undefined && localFormData.value.os !== null) {
    updateData.os = localFormData.value.os
  }
  if (localFormData.value.ram !== undefined && localFormData.value.ram !== null && localFormData.value.ram !== '') {
    updateData.ram = localFormData.value.ram
  }
  if (localFormData.value.storage !== undefined && localFormData.value.storage !== null && localFormData.value.storage !== '') {
    updateData.storage = localFormData.value.storage
  }
  if (localFormData.value.cpu !== undefined && localFormData.value.cpu !== null && localFormData.value.cpu !== '') {
    updateData.cpu = localFormData.value.cpu
  }
  if (localFormData.value.gpu !== undefined && localFormData.value.gpu !== null && localFormData.value.gpu !== '') {
    updateData.gpu = localFormData.value.gpu
  }
  if (localFormData.value.charger !== undefined && localFormData.value.charger !== null && localFormData.value.charger !== '') {
    updateData.charger = localFormData.value.charger
  }
  if (localFormData.value.comments !== undefined && localFormData.value.comments !== null && localFormData.value.comments !== '') {
    updateData.comments = localFormData.value.comments
  }
  const bulkValue = localFormData.value.value
  if (bulkValue !== undefined && bulkValue !== null && (bulkValue as any) !== '' && !Number.isNaN(bulkValue)) {
    updateData.value = bulkValue
  }
  if (localFormData.value.donor !== undefined && localFormData.value.donor !== null && localFormData.value.donor !== '') {
    updateData.donor = localFormData.value.donor
  }
  if (localFormData.value.donated_on !== undefined && localFormData.value.donated_on !== null && localFormData.value.donated_on !== '') {
    updateData.donated_on = localFormData.value.donated_on
  }

  emit('submit', updateData)
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_combobox = _resolveComponent("v-combobox")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((showModal).value = $event)),
    "max-width": "800px",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "modal-header" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, "Bulk Edit Equipment (" + _toDisplayString(__props.itemCount) + " items)", 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, { class: "pt-4" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_alert, {
                type: "info",
                variant: "tonal",
                class: "mb-4"
              }, {
                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                  _createTextVNode(" Only fill in the fields you want to update. Empty fields will be left unchanged. ", -1)
                ]))]),
                _: 1
              }),
              (__props.error)
                ? (_openBlock(), _createBlock(_component_v_alert, {
                    key: 0,
                    type: "error",
                    variant: "tonal",
                    class: "mb-4"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(__props.error), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_form, {
                ref_key: "formRef",
                ref: formRef
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_select, {
                            modelValue: localFormData.value.status,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localFormData.value.status) = $event)),
                            items: statusOptions,
                            label: "Status (optional)",
                            variant: "outlined",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.location,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localFormData.value.location) = $event)),
                            label: "Location (optional)",
                            variant: "outlined",
                            placeholder: "e.g., Storage Room A",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.rating,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localFormData.value.rating) = $event)),
                            modelModifiers: { number: true },
                            label: "Rating 1-10 (optional)",
                            type: "number",
                            min: "1",
                            max: "10",
                            variant: "outlined",
                            placeholder: "Rate 1-10",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_combobox, {
                            modelValue: localFormData.value.os,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localFormData.value.os) = $event)),
                            items: osOptions,
                            label: "Operating System (optional)",
                            variant: "outlined",
                            placeholder: "Select or type OS",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.ram,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((localFormData.value.ram) = $event)),
                            label: "RAM (optional)",
                            variant: "outlined",
                            placeholder: "e.g., 16GB",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.storage,
                            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localFormData.value.storage) = $event)),
                            label: "Storage (optional)",
                            variant: "outlined",
                            placeholder: "e.g., 512GB SSD",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.cpu,
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((localFormData.value.cpu) = $event)),
                            label: "CPU (optional)",
                            variant: "outlined",
                            placeholder: "e.g., Intel i5",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.gpu,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((localFormData.value.gpu) = $event)),
                            label: "GPU (optional)",
                            variant: "outlined",
                            placeholder: "e.g., Intel UHD",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.value,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((localFormData.value.value) = $event)),
                            modelModifiers: { number: true },
                            label: "Value ($) (optional)",
                            type: "number",
                            min: "0",
                            step: "0.01",
                            variant: "outlined",
                            prefix: "$",
                            placeholder: "e.g., 250.00",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.donated_on,
                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((localFormData.value.donated_on) = $event)),
                            label: "Donation Date (optional)",
                            type: "date",
                            variant: "outlined",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, { cols: "12" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_combobox, {
                            modelValue: localFormData.value.charger,
                            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((localFormData.value.charger) = $event)),
                            items: chargerOptions.value,
                            label: "Charger (optional)",
                            variant: "outlined",
                            placeholder: "Select or type charger",
                            clearable: ""
                          }, null, 8, ["modelValue", "items"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, { cols: "12" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_combobox, {
                            modelValue: localFormData.value.donor,
                            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((localFormData.value.donor) = $event)),
                            items: donorOptions.value,
                            label: "Donor (optional)",
                            variant: "outlined",
                            placeholder: "Person or organization",
                            clearable: ""
                          }, null, 8, ["modelValue", "items"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, { cols: "12" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_textarea, {
                            modelValue: localFormData.value.comments,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((localFormData.value.comments) = $event)),
                            label: "Comments (optional)",
                            variant: "outlined",
                            rows: "3",
                            placeholder: "Add notes or comments...",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 512)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                color: "grey",
                variant: "text",
                onClick: _cache[13] || (_cache[13] = ($event: any) => (_ctx.$emit('close'))),
                disabled: __props.submitting
              }, {
                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                  _createTextVNode(" Cancel ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_component_v_btn, {
                color: "primary",
                variant: "flat",
                onClick: handleSubmit,
                loading: __props.submitting
              }, {
                default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                  _createTextVNode(" Apply Changes ", -1)
                ]))]),
                _: 1
              }, 8, ["loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})